import React from "react";
import { useToast, VStack, FormControl, FormErrorMessage, HStack, Checkbox, CheckboxGroup, Stack, Switch, Text, Button } from "@chakra-ui/react";
import { StepperModal } from "../../../../_components/Modals";
import { ThemedFormLabel } from "../../../homeowners/hire/themed-form-label";
import { ThemedInput } from "../../../homeowners/hire/themed-input";
import { organizationInfoSchema, contactInfoSchema, renovationDetailsSchema, OrganizationInfoFormData, RenovationContactInfoFormData, RenovationDetailsFormData } from "./schema";
import { RenovationArea, SimpleDuration } from "../../../../__generated__/graphql";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { gql, useMutation } from "@apollo/client";
import { ArrowBackIcon, ArrowForwardIcon } from "@chakra-ui/icons";
import { normalizeString } from "lib/utils";

interface Props {
  isOpen: boolean;
  onClose: () => void;
  userType: "business" | "homeowner";
}

const REQUEST_RENOVATION_PROJECT = gql`
  mutation requestRenovationProject($renovationProjectRequestDto: RenovationProjectRequestDto!) {
    requestRenovationProject(renovationProjectRequestDto: $renovationProjectRequestDto)
  }
`;

export const RenovationProjectOutsourcingModal: React.FC<Props> = ({ isOpen, onClose, userType }) => {
  const toast = useToast();
  const [currentStep, setCurrentStep] = React.useState(1);
  const totalSteps = userType === "business" ? 3 : 2;

  const [requestRenovationProject, { loading }] = useMutation(REQUEST_RENOVATION_PROJECT);

  const orgForm = useForm<OrganizationInfoFormData>({
    resolver: zodResolver(organizationInfoSchema),
    defaultValues: { organization: "", roleInOrganization: "", industry: "" },
  });

  const contactForm = useForm<RenovationContactInfoFormData>({
    resolver: zodResolver(contactInfoSchema),
    defaultValues: { firstName: "", lastName: "", email: "", phone: "" },
  });

  const detailsForm = useForm<RenovationDetailsFormData>({
    resolver: zodResolver(renovationDetailsSchema),
    defaultValues: {
      projectLocation: "",
      intendedDuration: undefined,
      areas: [],
      otherArea: "",
      requiresStructuralChanges: false,
    },
  });

  const resetAll = () => {
    setCurrentStep(1);
    orgForm.reset();
    contactForm.reset();
    detailsForm.reset();
  };

  const handleSubmitAll = async () => {
    const organizationInfo = userType === "business" ? orgForm.getValues() : undefined;
    const contactInfo = contactForm.getValues();
    const details = detailsForm.getValues();

    try {
      await requestRenovationProject({
        variables: {
          renovationProjectRequestDto: {
            organizationInfo,
            contactInfo,
            projectLocation: details.projectLocation,
            intendedDuration: details.intendedDuration,
            areas: details.areas,
            otherArea: details.otherArea || undefined,
            requiresStructuralChanges: !!details.requiresStructuralChanges,
          },
        },
      });

      toast({
        title: "Request Submitted Successfully",
        description: "Thank you for your request. Our customer success team will contact you shortly.",
        status: "success",
        duration: 5000,
        isClosable: true,
      });
      resetAll();
      onClose();
    } catch (error) {
      toast({
        title: "Submission Failed",
        description: "Please try again or contact support if the issue persists.",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    }
  };

  const renderStep = () => {
    const isBusiness = userType === "business";
    if (isBusiness && currentStep === 1) {
      const { register, handleSubmit, formState: { errors } } = orgForm;
      return (
        <form onSubmit={handleSubmit(() => setCurrentStep(2))}>
          <VStack spacing={6} align="stretch">
            <FormControl isInvalid={!!errors.organization}>
              <ThemedFormLabel>Organization Name</ThemedFormLabel>
              <ThemedInput {...register("organization")} placeholder="Enter your organization name" />
              <FormErrorMessage>{errors.organization?.message}</FormErrorMessage>
            </FormControl>
            <FormControl isInvalid={!!errors.roleInOrganization}>
              <ThemedFormLabel>Your role in the organization</ThemedFormLabel>
              <ThemedInput {...register("roleInOrganization")} placeholder="e.g., Project Manager" />
              <FormErrorMessage>{errors.roleInOrganization?.message}</FormErrorMessage>
            </FormControl>
                          <FormControl isInvalid={!!errors.industry}>
                <ThemedFormLabel>Industry (optional)</ThemedFormLabel>
                <ThemedInput {...register("industry")} placeholder="Enter your industry" />
                <FormErrorMessage>{errors.industry?.message}</FormErrorMessage>
              </FormControl>
              <Button
                type="submit"
                size="lg"
                w="full"
                colorScheme="flatteredFlamingo"
                rightIcon={<ArrowForwardIcon />}
                isLoading={loading}
                loadingText="Processing..."
                mt={4}
              >
                Next
              </Button>
            </VStack>
          </form>
        );
    }

    const { register: regC, handleSubmit: subC, formState: { errors: eC } } = contactForm;
    if ((userType === "homeowner" && currentStep === 1) || (userType === "business" && currentStep === 2)) {
      return (
        <form onSubmit={subC(() => setCurrentStep(currentStep + 1))}>
          <VStack spacing={6} align="stretch">
            <HStack spacing={4}>
              <FormControl isInvalid={!!eC.firstName}>
                <ThemedFormLabel>First Name</ThemedFormLabel>
                <ThemedInput {...regC("firstName")} placeholder="Enter your first name" />
                <FormErrorMessage>{eC.firstName?.message}</FormErrorMessage>
              </FormControl>
              <FormControl isInvalid={!!eC.lastName}>
                <ThemedFormLabel>Last Name</ThemedFormLabel>
                <ThemedInput {...regC("lastName")} placeholder="Enter your last name" />
                <FormErrorMessage>{eC.lastName?.message}</FormErrorMessage>
              </FormControl>
            </HStack>
            <FormControl isInvalid={!!eC.email}>
              <ThemedFormLabel>Email Address</ThemedFormLabel>
              <ThemedInput type="email" {...regC("email")} placeholder="Enter your email" />
              <FormErrorMessage>{eC.email?.message}</FormErrorMessage>
            </FormControl>
                          <FormControl isInvalid={!!eC.phone}>
                <ThemedFormLabel>Phone Number</ThemedFormLabel>
                <ThemedInput type="tel" {...regC("phone")} placeholder="Enter your phone number" />
                <FormErrorMessage>{eC.phone?.message}</FormErrorMessage>
              </FormControl>
              <HStack spacing={4} mt={4}>
                {currentStep > 1 && userType === "business" && (
                  <Button
                    type="button"
                    onClick={() => setCurrentStep(currentStep - 1)}
                    leftIcon={<ArrowBackIcon />}
                    size="lg"
                    flex="1"
                    variant="outline"
                    isDisabled={loading}
                  >
                    Previous
                  </Button>
                )}
                <Button
                  type="submit"
                  size="lg"
                  flex="1"
                  colorScheme="flatteredFlamingo"
                  rightIcon={<ArrowForwardIcon />}
                  isLoading={loading}
                  loadingText="Processing..."
                >
                  Next
                </Button>
              </HStack>
            </VStack>
          </form>
        );
    }

    const { register, handleSubmit, watch, setValue, formState: { errors } } = detailsForm;
    const areas = watch("areas") as unknown as RenovationArea[];
    const requiresStructuralChanges = watch("requiresStructuralChanges");

    const toggleAreas = (value: string[]) => setValue("areas", value as any, { shouldValidate: true });

    return (
      <form onSubmit={handleSubmit(() => handleSubmitAll())}>
        <VStack spacing={6} align="stretch">
          <FormControl isInvalid={!!errors.projectLocation}>
            <ThemedFormLabel>Project Location</ThemedFormLabel>
            <ThemedInput
              {...register("projectLocation")}
              placeholder="Enter project location"
            />
            <FormErrorMessage>
              {errors.projectLocation?.message}
            </FormErrorMessage>
          </FormControl>

          <FormControl isInvalid={!!errors.intendedDuration}>
            <ThemedFormLabel>Intended Duration of Renovation</ThemedFormLabel>
            <select
              {...register("intendedDuration")}
              className="min-w-full chakra-input css-0"
              style={{ padding: "10px", border: "1px solid #E2E8F0" }}
            >
              <option value={SimpleDuration.Lt_1Month}>
                Less than 1 month
              </option>
              <option value={SimpleDuration.OneToThreeMonths}>
                1-3 months
              </option>
              <option value={SimpleDuration.ThreeToSixMonths}>
                3-6 months
              </option>
              <option value={SimpleDuration.Gt_6Months}>Over 6 months</option>
            </select>
            <FormErrorMessage>
              {errors.intendedDuration?.message}
            </FormErrorMessage>
          </FormControl>

          <FormControl isInvalid={!!errors.areas}>
            <ThemedFormLabel>
              Which areas of renovation are required?
            </ThemedFormLabel>
            <CheckboxGroup
              value={areas as any}
              onChange={(v) => toggleAreas(v as string[])}
            >
              <Stack direction="column">
                {Object.values(RenovationArea)
                  .filter((area) => area !== RenovationArea.Other)
                  .map((area) => (
                    <Checkbox key={area} value={area}>
                      {normalizeString(area)}
                    </Checkbox>
                  ))}
                <Checkbox value={RenovationArea.Other}>
                  {normalizeString(RenovationArea.Other)}
                </Checkbox>
              </Stack>
            </CheckboxGroup>
            <FormErrorMessage>
              {(errors as any).areas?.message}
            </FormErrorMessage>
          </FormControl>

          {areas?.includes(RenovationArea.Other) && (
            <FormControl isInvalid={!!errors.otherArea}>
              <ThemedFormLabel>Other area (please specify)</ThemedFormLabel>
              <ThemedInput
                {...register("otherArea")}
                placeholder="e.g., Exterior works"
              />
              <FormErrorMessage>{errors.otherArea?.message}</FormErrorMessage>
            </FormControl>
          )}

          <FormControl>
            <ThemedFormLabel>
              Will the renovation require structural changes?
            </ThemedFormLabel>
            <HStack>
              <Text>No</Text>
              <Switch
                isChecked={!!requiresStructuralChanges}
                onChange={(e) =>
                  setValue("requiresStructuralChanges", e.target.checked, {
                    shouldValidate: true,
                  })
                }
              />
              <Text>Yes</Text>
            </HStack>
          </FormControl>

          <HStack spacing={4} mt={4}>
            <Button
              type="button"
              onClick={() => setCurrentStep(currentStep - 1)}
              leftIcon={<ArrowBackIcon />}
              size="lg"
              flex="1"
              variant="outline"
              isDisabled={loading}
            >
              Previous
            </Button>
            <Button
              type="submit"
              size="lg"
              flex="1"
              colorScheme="flatteredFlamingo"
              rightIcon={<ArrowForwardIcon />}
              isLoading={loading}
              loadingText="Submitting..."
            >
              Submit Request
            </Button>
          </HStack>
        </VStack>
      </form>
    );
  };

  const titleForStep = () => {
    if (userType === "business") {
      switch (currentStep) {
        case 1: return "Tell us about your organization";
        case 2: return "Tell us about yourself";
        case 3: return "Project details";
        default: return "Renovation Project";
      }
    }
    switch (currentStep) {
      case 1: return "Tell us about yourself";
      case 2: return "Project details";
      default: return "Renovation Project";
    }
  };

  const handleClose = () => { resetAll(); onClose(); };

  const onNext = async () => {
    if (userType === "business") {
      if (currentStep === 1) { if (await orgForm.trigger()) setCurrentStep(2); return; }
      if (currentStep === 2) { if (await contactForm.trigger()) setCurrentStep(3); return; }
      if (currentStep === 3) { if (await detailsForm.trigger()) await handleSubmitAll(); return; }
    } else {
      if (currentStep === 1) { if (await contactForm.trigger()) setCurrentStep(2); return; }
      if (currentStep === 2) { if (await detailsForm.trigger()) await handleSubmitAll(); return; }
    }
  };

  const onPrev = () => { if (currentStep > 1) setCurrentStep((s) => s - 1); };

  return (
    <StepperModal
      isOpen={isOpen}
      onClose={handleClose}
      title={titleForStep()}
      onSubmit={onNext}
      currentStep={currentStep}
      totalSteps={totalSteps}
      isLoading={loading}
      onReset={resetAll}
      onPrevious={onPrev}
    >
      {renderStep()}
    </StepperModal>
  );
};

